/**
 * membership-proxy.js
 * Handles dynamic membership verification via Shopify App Proxy.
 * Only activates if proxy-gated elements exist on the page.
 */
(function() {
  'use strict';

  const PROXY_URL = '/a/rmitjx/status';
  const PROXY_GATE_SELECTOR = '[data-proxy-gate="true"]';

  /**
   * Check if any proxy-gated elements exist on the page.
   * @returns {boolean}
   */
  function hasProxyGatedElements() {
    return document.querySelectorAll(PROXY_GATE_SELECTOR).length > 0;
  }

  /*
   * DISABLED (May 2026). The live proxy call is unnecessary: the ADD_MEMBERSHIP
   * Flow is the authority and runs regardless, and resolve-club-membership.liquid
   * reads the same membership source server-side, so this round trip to the Hive
   * app added nothing. Left commented (not deleted) pending the proxy
   * re-evaluation session. To re-enable, uncomment this function and the two call
   * lines in init(), then delete `const isMember = false;`.
   *
   * Fetch membership status from app proxy. Returns {isMember: boolean}.
   *
   * async function fetchMembershipStatus() {
   *   try {
   *     let url = PROXY_URL;
   *     const clubIdElement = document.querySelector('[data-target-club-id]');
   *     if (clubIdElement) {
   *       const clubId = clubIdElement.getAttribute('data-target-club-id');
   *       if (clubId) {
   *         url += '?club_id=' + encodeURIComponent(clubId);
   *       }
   *     }
   *     const response = await fetch(url, {
   *       method: 'GET',
   *       credentials: 'include',
   *       headers: { 'Accept': 'application/json' }
   *     });
   *     if (!response.ok) {
   *       logToDebug('warn', 'Failed to fetch membership status: ' + response.status);
   *       return { isMember: false };
   *     }
   *     return await response.json();
   *   } catch (error) {
   *     logToDebug('error', 'Error fetching membership status: ' + error);
   *     return { isMember: false };
   *   }
   * }
   */

  /**
   * Resolve the membership question the server deferred, for the elements it
   * deferred it on.
   *
   * Scope is deliberately limited to elements carrying data-proxy-gate. Those
   * are the only ones eval-variant-visibility.liquid returned 'proxy' for, and
   * 'proxy' means exactly one thing: every other input to the decision (the
   * visitor's demographic tier, the product's visibility mode) has already been
   * applied server-side, and the single remaining unknown is whether this
   * visitor is a club member.
   *
   * This function used to walk every variant element on the page and re-derive
   * the whole gate from scratch, which let it contradict a decision the server
   * had already made correctly. Two live divergences came out of that, both in
   * docs/specs/completed/assets-membership-proxy--exclude-mode-client-gate-divergence.md:
   * an include-mode list mixing a demographic tier with a membership tier (e.g.
   * ['staff','member'], rendered visible server-side for a staff visitor) and an
   * exclude-mode list with no demographic tier at all (e.g. ['nonMember'],
   * likewise rendered visible) were both re-hidden client-side on every load.
   * Reading only the server's own gate attribute removes that entire class of
   * drift: the client can no longer overrule a variant the server chose to show,
   * in any mode or tier combination.
   *
   * @param {boolean} isMember - Whether the user is a member.
   */
  function unlockProxyGatedElements(isMember) {
    const variantData = getVariantData();

    // Only elements the server actually gated. product-form.liquid emits
    // data-proxy-gate on the swatch <li> (line 415) and the dropdown <option>
    // (line 516); nothing else in the theme emits it.
    // Note: regios-dopp-generic-variant-id-input is a legacy attribute from the Regios/DOPP
    // variant selector app, which is no longer installed. Kept here for historical compatibility.
    const gatedElements = document.querySelectorAll(PROXY_GATE_SELECTOR);

    gatedElements.forEach(element => {
      const variantId = getVariantIdFromElement(element);
      if (!variantId) return;

      const variant = variantData.find(v => String(v.id) === String(variantId));
      if (!variant) return;

      const tierList = variant.tier_visibility || [];

      // Skip variants without tier restrictions
      if (!tierList || tierList.length === 0) return;

      const shouldShow = evaluateVisibility(tierList, isMember);

      if (shouldShow) {
        // Show this variant
        element.style.display = '';
        element.classList.remove('proxy-gate');
        element.removeAttribute('data-proxy-gate');

        // If it's an option element (dropdown), enable it
        if (element.tagName === 'OPTION') {
          element.disabled = false;
        }
        
        logToDebug('log', `Showing variant ${variantId} for ${isMember ? 'member' : 'non-member'}`);
      } else {
        // Hide this variant (it's for the opposite tier)
        element.style.display = 'none';
        element.classList.add('proxy-gate');
        element.setAttribute('data-proxy-gate', 'true');

        // If it's an option element (dropdown), disable it
        if (element.tagName === 'OPTION') {
          element.disabled = true;
        }
        
        logToDebug('log', `Hiding variant ${variantId} (not for ${isMember ? 'member' : 'non-member'})`);
      }
    });
  }

  /**
   * Get visibility mode from JSON script.
   *
   * UNUSED as of 2026-08-06. evaluateVisibility() no longer consults the
   * visibility mode, because the mode is fully applied server-side before an
   * element is ever marked data-proxy-gate. Retained, not deleted: the whole of
   * this file is a deliberately kept fallback for the pre-eval-variant-visibility
   * gating method, and this reads a live server contract
   * (product-variants.liquid:96 still emits the visibility-mode script tag).
   * Flagged as a /prune-js lead in changelog.yml along with that now-orphaned
   * emitter; do not remove one without the other.
   *
   * @returns {string}
   */
  function getVisibilityMode() {
    const script = document.querySelector('script[data-name="visibility-mode"]');
    if (!script) return 'all';
    try {
      return JSON.parse(script.textContent) || 'all';
    } catch {
      return 'all';
    }
  }

  /**
   * Get variant data from JSON script.
   * @returns {Array}
   */
  function getVariantData() {
    const script = document.querySelector('script[data-name="variant-json"]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent) || [];
    } catch {
      return [];
    }
  }

  /**
   * Get membership context from server-side Liquid verification.
   * @returns {{isClubMember: boolean, verifiedServerSide: boolean, targetClubId: string|null}}
   */
  function getMembershipContext() {
    const script = document.querySelector('script[data-name="membership-context"]');
    if (!script) return { isClubMember: false, verifiedServerSide: false, targetClubId: null };
    try {
      return JSON.parse(script.textContent) || { isClubMember: false, verifiedServerSide: false, targetClubId: null };
    } catch {
      return { isClubMember: false, verifiedServerSide: false, targetClubId: null };
    }
  }

  /**
   * Extract variant ID from an element.
   * @param {HTMLElement} element
   * @returns {string|null}
   */
  function getVariantIdFromElement(element) {
    // Check for input inside li (swatch style)
    const input = element.querySelector('input[regios-dopp-generic-variant-id-input]');
    if (input) {
      return input.getAttribute('regios-dopp-generic-variant-id-input');
    }

    // Check for option element (dropdown style)
    if (element.tagName === 'OPTION') {
      return element.getAttribute('regios-dopp-generic-variant-id-input');
    }

    return null;
  }

  /**
   * Decide whether a proxy-gated variant should be revealed.
   *
   * Only ever called for elements the server marked data-proxy-gate, so the
   * demographic tier and the visibility mode have both already been applied and
   * are deliberately not re-checked here. That is not a shortcut: in
   * eval-variant-visibility.liquid the branch that runs after the demographic
   * check is character-for-character identical in include mode (lines 46-53)
   * and exclude mode (lines 63-70), so once an element is gated the mode cannot
   * change the answer. The server's own deferred rule, read off those lines, is
   * simply that a member sees 'member' variants and a non-member sees
   * 'nonMember' variants.
   *
   * The previous version took visibilityMode and, in exclude mode, returned the
   * negation of this. That was the bug: it inverted the answer for exactly the
   * variants the server had gated.
   *
   * @param {Array<string>} tierList
   * @param {boolean} isMember
   * @returns {boolean}
   */
  function evaluateVisibility(tierList, isMember) {
    if (!tierList || tierList.length === 0) return true;

    // A list carrying both tiers shows to everyone. The server resolves this to
    // 'visible' and never gates it, so this is a defensive default rather than a
    // reachable path.
    if (tierList.includes('member') && tierList.includes('nonMember')) return true;

    return tierList.includes(isMember ? 'member' : 'nonMember');
  }

  /**
   * Auto-select the first visible variant after form manipulation.
   * This ensures we don't show a hidden variant as the "default".
   */
  function autoSelectFirstVisibleVariant() {
    // Find visible variant options (swatches/radio buttons)
    // Uses data-proxy-gate attribute instead of style substring matching for robustness
    const visibleInputs = document.querySelectorAll(
      'li:not([data-proxy-gate]) input[regios-dopp-generic-variant-id-input]'
    );

    // Find visible dropdown options (main product form and the sticky bar)
    const dropdownSelects = document.querySelectorAll('select[name="id"], select[data-productid], select.productOption, select.sticky-bar-product-options');
    
    let selectedVariantId = null;

    // Try swatch/radio style first
    if (visibleInputs.length > 0) {
      const firstVisibleInput = visibleInputs[0];
      const variantId = firstVisibleInput.getAttribute('regios-dopp-generic-variant-id-input');
      
      // Check if current selection is hidden
      const currentlyChecked = document.querySelector('input[regios-dopp-generic-variant-id-input]:checked');
      const needsReselection = !currentlyChecked || (() => {
        const currentLi = currentlyChecked.closest('li');
        return currentLi && (
          currentLi.style.display === 'none' || 
          currentLi.hasAttribute('data-proxy-gate')
        );
      })();
      
      if (needsReselection) {
        // Select the first visible variant - use click() for swatch style
        // This matches how the theme handles it in common.js (productOption.click())
        firstVisibleInput.checked = true;
        firstVisibleInput.click();
        selectedVariantId = variantId;
        logToDebug('log', `Auto-selected first visible variant (swatch): ${variantId}`);
      }
    }

    // Try dropdown style
    dropdownSelects.forEach(select => {
      const visibleOptions = Array.from(select.options).filter(opt => 
        !opt.disabled && 
        opt.style.display !== 'none' && 
        !opt.hasAttribute('data-proxy-gate') &&
        opt.value
      );

      if (visibleOptions.length > 0) {
        const currentOption = select.options[select.selectedIndex];
        const isCurrentHidden = currentOption && (
          currentOption.disabled || 
          currentOption.style.display === 'none' ||
          currentOption.hasAttribute('data-proxy-gate')
        );

        if (isCurrentHidden || !currentOption || !currentOption.value) {
          // Select the first visible option and trigger change event
          // This matches how the theme handles it in common.js for dropdown variantStyle
          select.value = visibleOptions[0].value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          selectedVariantId = visibleOptions[0].value;
          logToDebug('log', `Auto-selected first visible variant (dropdown): ${selectedVariantId}`);
        }
      }
    });

    // Also update URL if variant ID was changed (Shopify convention)
    if (selectedVariantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', selectedVariantId);
      window.history.replaceState({}, '', url.toString());
      logToDebug('log', `Updated URL with variant: ${selectedVariantId}`);
    }
  }

  /**
   * Log message to debug console if available.
   * @param {string} type - 'log', 'warn', 'error'
   * @param {string} message
   */
  function logToDebug(type, message) {
    // Console log
    console[type](`[MembershipProxy] ${message}`);

    // UI Log
    const debugConsole = document.getElementById('visibility-debug-console');
    if (debugConsole) {
      const line = document.createElement('div');
      line.style.borderTop = '1px solid #ccc';
      line.style.padding = '2px 0';
      
      const time = new Date().toLocaleTimeString();
      let color = 'inherit';
      if (type === 'warn') color = 'orange';
      if (type === 'error') color = 'red';

      line.innerHTML = `<span style="color:#666">[${time}]</span> <span style="color:${color}">${message}</span>`;
      debugConsole.appendChild(line);
    }
  }

  /**
   * Initialize membership proxy logic.
   */
  async function init() {
    // Check if membership was already verified server-side (from customer.metafields.custom.memberships)
    const membershipContext = getMembershipContext();
    
    if (membershipContext.verifiedServerSide && membershipContext.isClubMember) {
      // User is verified as a MEMBER server-side, no need for proxy call
      // Visibility is already correctly handled by Liquid showing member variants
      logToDebug('log', 'Membership verified server-side: Member (no proxy needed)');
      autoSelectFirstVisibleVariant();
      return;
    }

    // Everyone not confirmed a member server-side falls through to here.
    //
    // This used to say the proxy could "potentially unlock member variants if
    // server-side data is stale". It cannot, and has not been able to since the
    // network fetch was disabled in May 2026: isMember is hardcoded false below,
    // so a genuine member inside the post-purchase staleness window (membership
    // not yet written by the ADD_MEMBERSHIP Flow, or a clubid mismatch) resolves
    // to non-member and their member variants stay hidden, with no client-side
    // correction. That is the accepted consequence of the kill-switch, recorded
    // here so the comment stops describing a capability that no longer exists.

    // Fallback: Server-side verification not available, check for proxy-gated elements
    if (!hasProxyGatedElements()) {
      logToDebug('log', 'No proxy-gated elements detected');
      return;
    }

    logToDebug('log', 'Proxy-gated elements detected; membership resolved server-side, proxy call disabled.');

    // Immediately select first visible variant based on initial visibility state
    // This ensures we don't show a hidden default while waiting for membership status
    autoSelectFirstVisibleVariant();

    // PROXY CALL DISABLED (May 2026) — INTENTIONAL DESIGN DECISION
    // Membership is resolved server-side in resolve-club-membership.liquid from
    // customer.metafields.custom.memberships. Confirmed members are handled server-side
    // and return early above. This proxy call is disabled because it added no value:
    // it asked the Hive app to read the same metaobject source we read server-side.
    // The Hive app has failed twice, causing site-wide gating failures. ITD confirmed
    // disabling it is the correct decision.
    //
    // Consequence while disabled: Anyone not confirmed as a member server-side (non-members,
    // post-purchase window before Flow writes membership, or clubid mismatch) stays non-member.
    // No network call corrects this. This is acceptable because the ADD_MEMBERSHIP Flow must
    // run anyway, and correcting mid-session adds complexity for no business gain.
    //
    // Do NOT re-enable without explicit business decision and stress testing.
    // Re-enable steps if ever needed: restore fetchMembershipStatus() call below and
    // delete the `const isMember = false;` line.
    // const { isMember } = await fetchMembershipStatus();
    // logToDebug('log', `Membership status from proxy: ${isMember ? 'Member' : 'Non-Member'}`);
    const isMember = false;

    unlockProxyGatedElements(isMember);
    
    // Auto-select first visible variant to ensure correct "default" display
    autoSelectFirstVisibleVariant();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
